import { backend } from '@/services/backend'
import { storage } from '@/helpers/storage'
import { Sentry } from '@/services/sentry'

export const CHANGELOG_STORAGE_KEY = 'sf_changelog'

interface ChangelogStorage {
  lastSyncDate?: string
  unreadCount?: number
}
import { Changelog } from './interfaces'
export class ChangelogView {
  private _unreadCount: number
  private _changelog?: Changelog[]

  /**
   * Parses the changelog storage data
   * @returns The parsed changelog storage data or undefined if invalid
   */
  private parseStorageData(): ChangelogStorage | undefined {
    const changelogDataString: string | null = storage.getItem(
      CHANGELOG_STORAGE_KEY,
    )
    if (!changelogDataString) {
      return undefined
    }

    try {
      const changelogData: ChangelogStorage = JSON.parse(changelogDataString)
      return changelogData
    } catch (e) {
      Sentry.captureException(e)
      return undefined
    }
  }

  constructor(unreadCount: number = 0) {
    const storageData = this.parseStorageData()
    this._unreadCount = storageData?.unreadCount ?? unreadCount
    this._changelog = undefined

    this.refreshIfNeeded()
  }

  /**
   * Returns the last sync date
   * @returns The last sync date or undefined if it is not set
   */
  lastSyncDate(): Date | undefined {
    const changelogDataString: string | null = storage.getItem(
      CHANGELOG_STORAGE_KEY,
    )

    if (!changelogDataString) {
      return undefined
    }

    try {
      const changelogData: ChangelogStorage = JSON.parse(changelogDataString)

      if (!changelogData || !changelogData.lastSyncDate) {
        return undefined
      }

      return new Date(changelogData.lastSyncDate)
    } catch (e) {
      Sentry.captureException(e)
      return undefined
    }
  }

  /**
   * Updates the last sync date
   * Returns the new date
   */
  updateState(): Date {
    const date = new Date().toISOString()
    const changelogData: ChangelogStorage = {
      lastSyncDate: date,
      unreadCount: this._unreadCount,
    }
    storage.setItem(CHANGELOG_STORAGE_KEY, JSON.stringify(changelogData))
    return new Date(date)
  }

  /**
   * Returns the number of unread changelog items
   */
  unreadCount(): number {
    if (!this._changelog) {
      return this._unreadCount
    }
    return this._changelog.filter(
      (item) => !item.is_read && !item.is_historical,
    ).length
  }

  async loadData(): Promise<void> {
    const data = await backend.getChangelog()
    this._changelog = data
  }

  /**
   * Marks a changelog item as read
   * @param item - The changelog item to mark as read
   */
  async mark_read(item: Changelog): Promise<void> {
    if (!this._changelog) {
      throw new Error('Changelog not loaded')
    }
    await backend.markReadChangelog(item.id)
    item.is_read = true
  }

  get data(): Changelog[] {
    if (!this._changelog) {
      throw new Error('Changelog not loaded')
    }
    return this._changelog
  }

  /**
   * Refreshes the changelog if needed
   * @param forceRefresh - If true, the changelog will be refreshed even if it is not needed
   */
  async refreshIfNeeded(forceRefresh: boolean = false): Promise<void> {
    if (this.needsSync() || forceRefresh) {
      this._unreadCount = await backend.getChangelogUnreadCount()
      this.updateState()
    }
  }

  /**
   * Checks if the changelog needs to be synced
   * @returns True if the changelog needs to be synced, false otherwise
   */
  needsSync(): boolean {
    // Sync every 5 days
    const days = 5

    const lastSyncDate = this.lastSyncDate()

    if (!lastSyncDate) {
      return true
    }

    return lastSyncDate < new Date(Date.now() - 1000 * 60 * 60 * 24 * days)
  }
}

let changelogView: ChangelogView | undefined

/**
 * Creates a new changelog view, or returns the existing one
 * @returns The changelog view
 */
export async function createChangelogView(): Promise<ChangelogView> {
  if (changelogView) {
    return changelogView
  }

  changelogView = new ChangelogView()
  await changelogView.refreshIfNeeded()
  return changelogView
}

/**
 * Resets the changelog view
 */
export function resetChangelogView(): void {
  changelogView = undefined
}
